/* =====================================================
   Damlab — Animation Lifecycle
   ----------------------------------------------------
   Centralized IntersectionObservers — one observer per
   behavior, shared across the page. Modules call
   App.animations.observeReveal(el) instead of creating
   their own observers, which prevents:
     - duplicate observers per element
     - re-init listener stacking after section reload
     - leaked observers when DOM is swapped out
   ===================================================== */
(function (App) {
  'use strict';
  if (!App) { console.error('[animations] App core not loaded'); return; }

  // ── Reveal: fade-up on scroll-into-view ─────────────
  let revealObserver = null;
  function ensureReveal() {
    if (revealObserver) return revealObserver;
    revealObserver = new IntersectionObserver((entries) => {
      // Stagger items entering in the same batch (mobile single-column)
      const visible = entries.filter(e => e.isIntersecting);
      visible.forEach((entry, i) => {
        const delay = i * 80;
        setTimeout(() => entry.target.classList.add('visible'), delay);
        revealObserver.unobserve(entry.target);
      });
    }, { threshold: 0.08, rootMargin: '0px 0px -20px 0px' });
    return revealObserver;
  }

  // ── Counter: count up from 0 to data-target ─────────
  let counterObserver = null;
  function ensureCounter() {
    if (counterObserver) return counterObserver;
    counterObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        const el = entry.target;
        const target = parseInt(el.dataset.target, 10);
        if (!Number.isFinite(target)) return;
        const suffix = el.dataset.suffix ?? '+';
        const duration = 1800;
        const step = Math.max(1, Math.ceil(target / (duration / 16)));
        let current = 0;
        const tick = () => {
          current = Math.min(current + step, target);
          el.textContent = current + suffix;
          if (current < target) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
        counterObserver.unobserve(el);
      });
    }, { threshold: 0.5 });
    return counterObserver;
  }

  // ── Generic threshold observer (one-shot class toggle) ─
  // Used by waterfilter bars + similar effects.
  function onceVisible(el, cb, options) {
    const obs = new IntersectionObserver((entries, o) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          cb(entry.target);
          o.unobserve(entry.target);
        }
      });
    }, { threshold: 0.3, ...options });
    obs.observe(el);
    return () => obs.disconnect();
  }

  // ── Public API ──────────────────────────────────────
  App.animations = {
    /** Observe an element to receive .visible when it enters view. */
    observeReveal(el) {
      if (!el || el.classList.contains('visible')) return;
      ensureReveal().observe(el);
    },
    /** Observe many. */
    observeRevealAll(scope = document) {
      scope.querySelectorAll('.reveal:not(.visible)').forEach(el => ensureReveal().observe(el));
    },
    /** Re-trigger reveal on a freshly-shown subtree (e.g., tab switch). */
    retriggerReveal(scope) {
      if (!scope) return;
      scope.querySelectorAll('.reveal').forEach(el => {
        el.classList.remove('visible');
        // double rAF so the browser registers the removal first
        requestAnimationFrame(() => requestAnimationFrame(() => ensureReveal().observe(el)));
      });
    },
    /** Observe counters in scope. */
    observeCounters(scope = document) {
      scope.querySelectorAll('.counter[data-target]').forEach(el => ensureCounter().observe(el));
    },
    onceVisible
  };

  // ── Auto-mount: page-wide reveal + counters ─────────
  // Registered as a module so it participates in the lifecycle.
  App.register('animations.global', {
    mount(ctx) {
      App.animations.observeRevealAll(ctx.root);
      App.animations.observeCounters(ctx.root);
      return () => {
        // Disconnect singletons on full unmount (rare, mostly SPA-style).
        revealObserver?.disconnect();  revealObserver = null;
        counterObserver?.disconnect(); counterObserver = null;
      };
    }
  });

  // Wire to every page.
  App.route('*', ['animations.global']);

})(window.App);
